import { LockTwoTone, WarningFilled } from "@ant-design/icons";
import { showError, showSuccess } from "common/ToastNotifications";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import SuccessResult from "components/atoms/SuccessResult";
import AdminTenantForm from "components/molecules/AdminTenantForm";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import TenantActionConfirmationModal from "components/molecules/TenantActionConfirmationModal";
import AppPaths from "constants/appPaths";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";
import AdminTenantUsers from "./AdminTenantUsers";

export class AdminTenantOverview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showTenantDisableConfirmation: false,
            showTenantDeleteConfirmation: false,
            showTenantDeletedSuccessResult: false,
            fetchingTenant: false,
            permissionDenied: false,
            busy: false,
            busyMessage: "Fetching tenant...",
        };
        props.instance.current = this;
    }

    componentWillMount() {
        this.setState({
            fetchingTenant: true,
        });
        this.props.getTenantById(this.props.tenantId);
    }

    componentDidMount() {
        this.showBreadCrumbNavigationItems();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.getTenantByIdResultChanged(prevProps);
        this.updateTenantResultChanged(prevProps);
        this.deleteTenantResultChanged(prevProps);
    }

    componentWillUnmount() {}

    getTenantByIdResultChanged = (prevProps) => {
        if (this.props.getTenantByIdResult && this.props.getTenantByIdResult !== prevProps.getTenantByIdResult) {
            if (!this.props.getTenantByIdResult.success) {
                if (this.props.getTenantByIdResult.code === "PERMISSION_DENIED") {
                    this.setState({
                        permissionDenied: true,
                    });
                } else {
                    showError("Unable to get tenant at this moment.");
                }
            } else {
                this.props.onFetchTenant(this.props.tenant);
            }
            this.setState({
                fetchingTenant: false,
            });
        }
    };

    updateTenantResultChanged = (prevProps) => {
        if (this.props.updateTenantResult && this.props.updateTenantResult !== prevProps.updateTenantResult) {
            if (!this.props.updateTenantResult.success) {
                if (this.props.updateTenantResult.code === "PERMISSION_DENIED") {
                    showError("Permission Denied");
                    if (this.state.disablingTenant) {
                        this.props.onFetchTenant(this.props.tenant);
                    } else if (this.state.enablingTenant) {
                        this.props.onFetchTenant(this.props.tenant);
                    }
                } else {
                    if (this.state.disablingTenant) {
                        showError("Unable to disable tenant at this moment.");
                        this.props.onFetchTenant(this.props.tenant);
                    } else if (this.state.enablingTenant) {
                        showError("Unable to enable tenant at this moment.");
                        this.props.onFetchTenant(this.props.tenant);
                    } else if (this.state.switchingGoldenMaintenance) {
                        showError("Unable to change Golden Nominal maintenance at this moment.");
                    } else {
                        showError("Unable to update tenant at this moment.");
                    }
                }
            } else {
                if (this.state.disablingTenant) {
                    showSuccess("Tenant disabled successfully");
                    this.props.onFetchTenant({ ...this.props.tenant, enabled: false });
                } else if (this.state.enablingTenant) {
                    showSuccess("Tenant enabled successfully");
                    this.props.onFetchTenant({ ...this.props.tenant, enabled: true });
                } else if (this.state.switchingGoldenMaintenance) {
                    showSuccess("Golden Nominal maintenance updated");
                    // Saved as sent: the switch shows it at once, with no re-read that could fail.
                    this.props.tenantFeaturesSaved(this.props.tenantId, { goldenMaintenance: this.state.goldenMaintenanceRequested });
                } else {
                    showSuccess("Tenant updated successfully");
                }
            }
            this.setState({
                busy: false,
                disablingTenant: false,
                enablingTenant: false,
                switchingGoldenMaintenance: false,
                busyMessage: "",
            });
        }
    };

    deleteTenantResultChanged = (prevProps) => {
        if (this.props.deleteTenantResult && this.props.deleteTenantResult !== prevProps.deleteTenantResult) {
            if (!this.props.deleteTenantResult.success) {
                if (this.props.deleteTenantResult.code === "PERMISSION_DENIED") {
                    showError("Permission Denied");
                } else {
                    showError("Unable to delete tenant at this moment.");
                }
            } else {
                this.setState({
                    showTenantDeletedSuccessResult: true,
                });
            }
            this.setState({
                busy: false,
                busyMessage: "",
            });
        }
    };

    disableTenant = () => {
        this.setState({
            showTenantDisableConfirmation: true,
        });
    };

    onDisableTenantCancel = () => {
        this.setState({
            showTenantDisableConfirmation: false,
        });
    };

    onDisableTenantConfirmed = () => {
        this.setState({
            showTenantDisableConfirmation: false,
            busy: true,
            disablingTenant: true,
            busyMessage: "Disabling tenant...",
        });
        this.props.onFetchTenant(null);
        this.props.updateTenant(this.props.tenantId, { enabled: false });
    };

    enableTenant = () => {
        this.setState({
            busy: true,
            enablingTenant: true,
            busyMessage: "Enabling tenant...",
        });
        this.props.onFetchTenant(null);
        this.props.updateTenant(this.props.tenantId, { enabled: true });
    };

    setGoldenMaintenance = (tenantId, on) => {
        this.setState({
            busy: true,
            switchingGoldenMaintenance: true,
            goldenMaintenanceRequested: on,
            busyMessage: on ? "Turning Golden Nominal maintenance on..." : "Turning Golden Nominal maintenance off...",
        });
        this.props.updateTenant(tenantId, { features: { goldenMaintenance: on } });
    };

    updateTenant = (tenantId, updatedValues) => {
        this.setState({
            busy: true,
            busyMessage: "Updating tenant...",
        });
        this.props.updateTenant(tenantId, updatedValues);
    };

    deleteTenant = () => {
        this.setState({
            showTenantDeleteConfirmation: true,
        });
    };

    onDeleteTenantCancel = () => {
        this.setState({
            showTenantDeleteConfirmation: false,
        });
    };

    onDeleteTenantConfirmed = () => {
        this.setState({
            showTenantDeleteConfirmation: false,
            busy: true,
            busyMessage: "Deleting tenant...",
        });
        this.props.deleteTenant(this.props.tenantId);
    };

    goToTenantAdminConsole = () => {
        this.props.history.push(AppPaths.ADMIN_CONSOLE);
    };

    fetchInProgress = () => {
        let isBusy = this.state.fetchingTenant;
        return isBusy;
    };

    isBusy = () => {
        return this.state.busy || this.fetchInProgress();
    };

    showBreadCrumbNavigationItems = () => {
        if (this.props.setBreadcrumbNavigationItems) {
            let breadCrumbItems = [
                {
                    route: AppPaths.ADMIN_CONSOLE,
                    text: "Admin Console",
                },
                {
                    route: "",
                    text: "Tenant Overview",
                },
                {
                    route: "",
                    text: this.props.tenantId,
                },
            ];

            this.props.setBreadcrumbNavigationItems(breadCrumbItems);
        }
    };

    render() {
        return (
            <FlexContainerLayout
                header={
                    !this.fetchInProgress() ? (
                        <>
                            {this.state.permissionDenied ? (
                                <ProhibitedArea></ProhibitedArea>
                            ) : (
                                <>
                                    <TenantActionConfirmationModal
                                        title="Disable the Tenant"
                                        icon={<LockTwoTone style={{ fontSize: "20px" }}></LockTwoTone>}
                                        visible={this.state.showTenantDisableConfirmation}
                                        description={[
                                            "Do you wish to disable the Tenant?",
                                            "You will need to enter the name of the tenant exactly as it is stored in order to proceed",
                                            "The tenant should be disabled before the tenant is deleted.",
                                        ]}
                                        tenant={this.props.tenant.company}
                                        onOk={this.onDisableTenantConfirmed}
                                        onCancel={this.onDisableTenantCancel}
                                    ></TenantActionConfirmationModal>
                                    <TenantActionConfirmationModal
                                        title="Delete Tenant Account"
                                        icon={<WarningFilled style={{ color: "orange", fontSize: "20px" }}></WarningFilled>}
                                        visible={this.state.showTenantDeleteConfirmation}
                                        description={[
                                            "Deleting a tenant account is permanent, all data  will be deleted and all users will be offboarded",
                                            "Do you need to back this tenant account up?",
                                            "The tenant account is currently disabled, you may proceed with deletion.",
                                            "You will need to enter the Tenant name exactly as it is stored to proceed with the tenancy deletion",
                                        ]}
                                        tenant={this.props.tenant.company}
                                        onOk={this.onDeleteTenantConfirmed}
                                        onCancel={this.onDeleteTenantCancel}
                                    ></TenantActionConfirmationModal>
                                    <SuccessResult
                                        show={this.state.showTenantDeletedSuccessResult}
                                        title="Delete Tenant Account"
                                        subTitle="Tenant account deletion is initiated successfully."
                                        onOkClick={this.goToTenantAdminConsole}
                                    ></SuccessResult>
                                    <AdminTenantForm
                                        tenant={this.props.tenant}
                                        onUpdateTenant={this.updateTenant}
                                        onSetGoldenMaintenance={this.setGoldenMaintenance}
                                        switchingGoldenMaintenance={!!this.state.switchingGoldenMaintenance}
                                    ></AdminTenantForm>
                                </>
                            )}
                        </>
                    ) : undefined
                }
            >
                <LoadingOverlay busy={this.isBusy()} spinner message={this.state.busyMessage || "Please wait..."}></LoadingOverlay>
                <AdminTenantUsers tenantId={this.props.tenantId}></AdminTenantUsers>
            </FlexContainerLayout>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        getTenantByIdResult: state.adminConsole.getTenantByIdResult,
        tenant: state.adminConsole.tenant,
        updateTenantResult: state.adminConsole.updateTenantResult,
        deleteTenantResult: state.adminConsole.deleteTenantResult,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getTenantById: (tenantId) => dispatch(actions.getTenantByIdRequest(tenantId)),
        updateTenant: (tenantId, updatedValues) => dispatch(actions.updateTenantRequest(tenantId, updatedValues)),
        tenantFeaturesSaved: (tenantId, features) => dispatch(actions.adminTenantFeaturesSaved(tenantId, features)),
        deleteTenant: (tenantId) => dispatch(actions.deleteTenantRequest(tenantId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AdminTenantOverview));
